import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("authUser");
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      localStorage.removeItem("authUser");
      return null;
    }
  });
  const [loading, setLoading] = useState(Boolean(localStorage.getItem("authToken")));

  useEffect(() => {
    let active = true;

    if (!localStorage.getItem("authToken")) {
      setLoading(false);
      return undefined;
    }

    api
      .get("/auth/me")
      .then((data) => {
        if (active) {
          setUser(data.user);
          localStorage.setItem("authUser", JSON.stringify(data.user));
        }
      })
      .catch(() => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        if (active) {
          setUser(null);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => {
    const saveSession = (data) => {
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("authUser", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    };

    return {
      user,
      loading,
      isAuthenticated: Boolean(user),
      isAdmin: user?.role === "admin",
      login: async (credentials) => saveSession(await api.post("/auth/login", credentials)),
      register: async (payload) => saveSession(await api.post("/auth/register", payload)),
      logout: () => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        setUser(null);
      },
      setUser,
    };
  }, [loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
